<template>
	<div class="grid">
		<template v-for="(node, i) in nodes">
			<template v-if="isOrNode(node)">
				<!-- OR node doesn't use icon, but text instead -->
				<div :key="`or--${node.guid}`" class="or">and</div>

				<!-- Recursive render -->
				<scene-grid
					:key="`grid--${node.guid}`"
					:nodes="node.children"
					:fragment="fragment"
					:error-fragment="errorFragment"
					:fn-edit-node="fnEditNode"
					:fn-remove-node="fnRemoveNode"
					bool="or"
				/>
			</template>
			<template v-else>
				<v-icon
					:key="`icon--${node.guid}`"
					:color="iconColor(node)"
					class="icon"
					size="22"
				>
					{{ icon(node) }}
				</v-icon>
				<component
					:is="node.valid ? fragment : errorFragment"
					:key="`fragment--${node.guid}`"
					:is-first="i == 0"
					:bool="bool"
					:node="node"
					:fn-edit="() => fnEditNode(node)"
				/>
			</template>

			<div :key="`menus--${node.guid}`" class="menu">
				<template v-if="isOrNode(node)">
					<AddConditionMenu v-bind="orMenuOptions(node)" />

					<!-- No "edit" menu item for OR nodes -->
					<FragmentMenu :node="node" :fn-remove="() => fnRemoveNode(node)" />
				</template>
				<template v-else>
					<FragmentMenu
						:node="node"
						:fn-edit="() => fnEditNode(node)"
						:fn-move-to-top="fnMoveToTop"
						:fn-move-up="fnMoveUp"
						:fn-move-down="fnMoveDown"
						:fn-move-to-bottom="fnMoveToBottom"
						:fn-remove="() => fnRemoveNode(node)"
					/>
				</template>
			</div>
		</template>
	</div>
</template>

<style lang="scss" scoped src="./Grid.scss"></style>

<script>
import * as k4util from "@/utils/k4util";

import AddConditionMenu from "../menu/AddConditionMenu";
import FragmentMenu from "../menu/FragmentMenu";

export default {
	// We have to give it a name here in order for it to be able to render itself
	// recursively.
	name: "SceneGrid",

	components: { AddConditionMenu, FragmentMenu },

	props: {
		nodes: {
			type: Array,
			required: true,
		},
		fragment: {
			type: Object,
			required: true,
		},
		errorFragment: {
			type: Object,
			required: true,
		},
		bool: {
			type: String,
			default: "and",
		},

		// Context (ellipsis) menu handlers
		fnEditNode: { type: Function, required: false },
		fnMoveToTop: { type: Function, required: false },
		fnMoveUp: { type: Function, required: false },
		fnMoveDown: { type: Function, required: false },
		fnMoveToBottom: { type: Function, required: false },
		fnRemoveNode: { type: Function, required: true },

		// Nested add (Or block) menu handlers
		fnAddDate: { type: Function, required: false },
		fnAddTime: { type: Function, required: false },
		fnAddInterval: { type: Function, required: false },
		fnAddVarCondition: { type: Function, required: false },
		fnAddEventCondition: { type: Function, required: false },
	},

	methods: {
		icon(node) {
			const target = node.target || node;
			if (node.valid) return k4util.deviceFAIcon(target);
			else return "fas fa-exclamation-triangle";
		},

		iconColor(node) {
			return node.valid ? "ocean lighten-1" : "rust-red";
		},

		isOrNode(node) {
			return node.cls === k4util.DEVICE_CLASS_OR;
		},

		orMenuOptions(node) {
			if (node.name.startsWith("Or when"))
				return {
					node,
					fnAddDate: () => this.fnAddDate(node),
					fnAddTime: () => this.fnAddTime(node),
					fnAddInterval: () => this.fnAddInterval(node),
				};
			if (node.name.startsWith("Or"))
				return {
					node,
					fnAddVarCondition: () => this.fnAddVarCondition(node),
					fnAddEventCondition: () => this.fnAddEventCondition(node),
				};
			return {};
		},
	},
};
</script>
